<template>
  <div class="nx-entity-list-editor">

    <nx-relation-editor-toolbar :title="label">
      <template slot="actions" v-if="entityWizard">
        <v-tooltip bottom>
          <v-btn slot="activator" icon small @click="removeAll" :disabled="disabled || loading">
            <v-icon>delete</v-icon>
          </v-btn>
          <span>{{ $t('message.common.deleteAll') }}</span>
        </v-tooltip>

        <v-tooltip v-if="searcher" bottom>
          <v-btn slot="activator" icon small @click="search" :disabled="limitReached || disabled || loading">
            <v-icon>search</v-icon>
          </v-btn>
          <span>{{ $t('message.common.search') }}</span>
        </v-tooltip>

        <v-tooltip bottom>
          <v-btn slot="activator" icon small @click="openWizard" :disabled="limitReached || disabled || loading">
            <v-icon>add</v-icon>
          </v-btn>
          <span>{{ $t('message.common.add') }}</span>
        </v-tooltip>
      </template>
    </nx-relation-editor-toolbar>

    <v-list three-line class="mt-0 pt-0 mb-3">
      <transition-group name="children-list" tag="div" ref="relations">
        <template v-for="(item, index) in value">
          <component
            :key="item.id"
            :is="item.list ? 'nx-entity-list-editor-item' : 'nx-entity-list-editor-legacy-item'"
            :item="item"
            :cloneable="cloneable"
            :sortable="sortable"
            :target="target"
            :entity="entity"
            :disabled="disabled"
            :open-modal-editor="openModalEditor"
            :deletable="deletable"
            @click="onEntityClick(item)"
            @moveUp="move(index, index - 1)"
            @moveDown="move(index, index + 1)"
            @clone="clone(item.id)"
            @remove="remove(item.id)"
          />
        </template>
        <nx-entity-list-editor-item-empty v-if="empty" key="empty"/>
      </transition-group>
    </v-list>

    <nx-entity-wizard
      v-if="wizardDialogState.render"
      :active="wizardDialogState.active"
      :entity="entity"
      :custom-config="wizardFieldsConfig"
      :merge-fields-config="mergeWizardFieldsConfig"
      :additional-payload="wizardAdditionalPayload"
      @dismiss="closeWizard"
      @entityCreated="onEntityCreated"
    />

    <nx-entity-modal-editor-dialog
      v-if="openModalEditor"
      :active="isModalEditorActive"
      :entity="selectedEntity"
      :type="entity"
      :parent-title="getModalTitle()"
      v-bind="selectedEntity"
      @dismiss="closeModalEditor"
    />

    <nx-searcher-dialog
      v-if="isSearcherEnabled"
      :active="searcherDialogState.active"
      :entity="searcher"
      :entityTabs="false"
      :filters="searcherFilters"
      :limit="limit"
      :value="value"
      @dismiss="closeSearcher"
      @input="acceptSearcherValue"
      title="Buscador"
    />
  </div>
</template>

<script>
import apiContent from '@/modules/content/editor/api';
import NxRelationEditorToolbar from '@/modules/relation/editor/components/Toolbar';
import NxEntityWizard from '@/modules/entity/wizard/Wizard';
import HasEntityFields from '@/modules/form/mixins/has-entity-fields';
import { mapGetters } from 'vuex';
import NxEntityModalEditorDialog from '@/modules/entity/editor/EntityModalEditorDialog';
import NxSearcherDialog from '@/modules/searcher/SearcherDialog';
import NxEntityListEditorItem from '@/modules/entity/list/EntityListEditorItem';
import NxEntityListEditorItemEmpty from '@/modules/entity/list/EntityListEditorItemEmpty';
import NxEntityListEditorLegacyItem from '@/modules/entity/list/EntityListEditorLegacyItem';

export default {
  name: 'nx-entity-list-editor',
  mixins: [HasEntityFields],
  components: {
    NxEntityListEditorLegacyItem,
    NxEntityListEditorItemEmpty,
    NxEntityListEditorItem,
    NxSearcherDialog,
    NxEntityModalEditorDialog,
    NxRelationEditorToolbar,
    NxEntityWizard,
  },
  inject: {
    $getParentEditor: {
      default: null,
    },
  },
  props: {
    entity: {
      type: String,
      required: true,
    },
    value: {
      type: Array,
      required: true,
    },
    label: {
      type: String,
    },
    limit: {
      type: Number,
    },
    sortable: {
      type: Boolean,
    },
    cloneable: {
      type: Boolean,
    },
    searcher: {
      type: String,
    },
    deletable: Boolean,
    searcherFilters: {
      type: Object,
      default: () => ({}),
    },
    disabled: {
      type: Boolean,
    },
    openEntityEditor: {
      type: Boolean,
      default: true,
    },
    openModalEditor: {
      type: Boolean,
    },
    target: {
      type: String,
      default: '_self',
    },
    wizardFieldsConfig: {
      type: Array,
    },
    mergeWizardFieldsConfig: {
      type: Array,
      default: () => ([]),
    },
  },
  data() {
    return {
      wizardDialogState: {
        active: false,
        render: false,
      },
      searcherDialogState: {
        active: false,
        render: false,
      },
      selectedEntity: null,
    };
  },
  computed: {
    ...mapGetters(['getEntityConfig', 'getWizard', 'availableContentTypes']),
    parentEntityId() {
      return this.$getParentEditor().id;
    },
    parentEntityVersionId() {
      return this.$getParentEditor().versionId;
    },
    parentEntityType() {
      return this.$getParentEditor().type || this.$getParentEditor().entity;
    },
    limitReached() {
      if (!this.limit) return false;
      return this.value.length >= this.limit;
    },
    empty() {
      return this.value.length === 0;
    },
    loading() {
      return false;
    },
    entityWizard() {
      return this.getWizard(this.entity);
    },
    wizardAdditionalPayload() {
      const payload = {
        parentEntity: this.parentEntityType,
        parentEntityId: this.parentEntityId,
      };
      if (this.parentEntityVersionId) {
        payload.parentEntityVersionId = this.parentEntityVersionId;
      }
      return payload;
    },
    isModalEditorActive() {
      return this.selectedEntity !== null;
    },
    isSearcherEnabled() {
      return this.searcher && this.searcherDialogState.render;
    },
  },
  methods: {
    getModalTitle() {
      return this.$getParentEditor().description || this.$getParentEditor().title;
    },
    openWizard() {
      this.wizardDialogState.render = true;
      this.$nextTick(() => {
        this.wizardDialogState.active = true;
      });
    },
    closeWizard() {
      this.wizardDialogState.active = false;
      this.$nextTick(() => {
        this.wizardDialogState.render = false;
      });
    },
    openSearcher() {
      this.searcherDialogState.render = true;
      this.$nextTick(() => {
        this.searcherDialogState.active = true;
      });
    },
    closeSearcher() {
      this.searcherDialogState.active = false;
      this.$nextTick(() => {
        this.searcherDialogState.render = false;
      });
    },
    async clone(id) {
      // If the type is a content, return a route to the content editor.
      if (this.availableContentTypes.includes(this.entity)) {
        return this.cloneContent(id, this.entity);
      }
      return this.cloneEntity();
    },
    async cloneEntity(id, type) {
      console.error('Entity clone is not implemented', { id, type });
    },
    async cloneContent(id, type) {
      this.toast.notify('message.content.tools.cloning');
      try {
        const result = await apiContent.cloneContent(id, type);
        this.toast.notify('message.content.tools.clonedSuccess');
        this.$emit('input', [...this.value, result]);
      } catch (error) {
        this.toast.notify('message.content.tools.clonedFail');
        console.error(error);
      }
    },
    remove(id) {
      this.$emit('input', this.value.filter(item => item.id !== id));
    },
    removeAll() {
      this.$emit('input', []);
    },
    move(from, to) {
      const clone = [...this.value];
      clone.splice(to, 0, clone.splice(from, 1)[0]);
      this.$emit('input', clone);
    },
    search() {
      this.openSearcher();
    },
    closeModalEditor() {
      this.selectedEntity = null;
    },
    onEntityClick(item) {
      if (this.disabled) {
        return;
      }
      if (this.openModalEditor) {
        this.selectedEntity = item;
      }
    },
    onEntityCreated(entity) {
      if (this.openModalEditor) {
        this.selectedEntity = entity;
        return this.$emit('input', [...this.value, entity]);
      }

      if (this.openEntityEditor
        && entity
        && entity.route
        && entity.navigateToEntityEditor
      ) {
        return this.$router.push(entity.route);
      }

      return this.$emit('input', [...this.value, entity]);
    },
    acceptSearcherValue(value) {
      this.$emit('input', value);
    },
  },
};
</script>

<style lang="stylus">
.nx-entity-list-editor {
  .nx-entity-list-editor-legacy-item {
    .v-list__tile {
      height: 88px;
      padding: 0;
      margin-top: 8px;
    }

    .badges {
      min-height: 16px;
    }

    &.sortable .v-list__tile {
      min-height: 122px;
    }

    &.cloneable .v-list__tile {
      height: 144px;
    }
  }

  .nx-entity-list-editor-item {
    margin-top: 8px;
  }
}

.children-list-move {
  transition: transform .5s;
}

.children-list-enter, .children-list-leave-to {
  opacity: 0;
  transform: translateX(30px);
}
</style>
